"use client";
import { useThemeStore } from "@/store";
import { ThemeProvider } from "next-themes";
import { cn } from "@/lib/utils";
import { Toaster as ReactToaster } from "@/components/ui/toaster";
import { Toaster } from "react-hot-toast";
import { SonnToaster } from "@/components/ui/sonner";
import { usePathname } from "next/navigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, useEffect } from "react";
import { LocaleProvider } from "./locale-provider";

const Providers = ({ children }: { children: React.ReactNode }) => {
  const { theme, radius } = useThemeStore();
  const location = usePathname();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  // Apply body classes and styles dynamically
  useEffect(() => {
    const body = document.body;
    const baseClasses = body.className.split(" ").filter(cls => 
      cls === "dash-tail-app" || cls.startsWith("inter-")
    ).join(" ");
    
    if (location === "/") {
      body.className = baseClasses;
      body.style.removeProperty("--radius");
    } else {
      body.className = cn(baseClasses, `theme-${theme}`);
      body.style.setProperty("--radius", `${radius}rem`);
    }
  }, [location, theme, radius]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        enableSystem={false}
        defaultTheme="light"
      >
        <LocaleProvider>
          <div className={cn("h-full  ")}>
            {children}
            <ReactToaster />
          </div>
        </LocaleProvider>
        <Toaster />
        <SonnToaster />
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default Providers;
